import { type RepoItem } from "@/components/features/matter/file-repo/FileRepository";
import { FileViewer } from "@/components/features/matter/file-repo/FileViewer";
import { FolderTree } from "@/components/features/matter/file-repo/FolderTree";
import { CreateDocxSlideover } from "@/components/features/matter/file-repo/CreateDocxSlideover";
import {
  ActionConfirmationModal,
  Badge,
  Button,
  Input,
  ScrollHint,
  Skeleton,
  Text,
  Typography,
} from "@/components/ui";
import { RepoUploadSplitButton } from "@/components/ui/RepoUploadSplitButton/RepoUploadSplitButton";
import { FileBulkActions } from "@/components/ui/FileBulkActions";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { useUser } from "@/components/UserProvider";
import {
  retrieveMatterTemplatesQueryOptions,
  useBulkDeleteMatterTemplates,
  useCreateMatterTemplate,
  useDeleteMatterTemplate,
} from "@/lib/data/react-query/matters/matter-template";
import { generateS3ItemPreSignedUrlV1 } from "@/lib/data/requests/s3";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterTemplate } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import {
  getFileIcon,
  getFileNameParts,
  handleDownload,
} from "@/lib/utils/matterFile";
import { ErrorBoundary } from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import { downloadZip } from "@/lib/utils/downloadZip";
import {
  File as FileIcon,
  FilesIcon,
  FileText,
  Folder,
  CopyPlus,
  Download,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { AddOrEditTemplateModal } from "./AddOrEditTemplateModal";
import { BulkUploadMatterTemplatesModal } from "./BulkUploadMatterTemplatesModal";
import { DuplicateTemplateModal } from "./DuplicateTemplateModal";
import { EditDocxTemplateModal } from "./EditDocxTemplateModal";
import { MatterTemplateContextMenu } from "./MatterTemplateContextMenu";

const ExplorerSkeleton = ({ rows = 8 }: { rows?: number }) => {
  const widths = ["55%", "72%", "48%", "64%", "58%", "68%", "52%", "61%"];

  return (
    <div className="py-1">
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={`template-explorer-skeleton-${index}`}
          className="flex items-center gap-2 px-2 py-1"
        >
          <Skeleton className="h-4 w-4 shrink-0 rounded-sm" />
          <Skeleton
            className="h-4 rounded-sm"
            style={{ width: widths[index % widths.length] }}
          />
        </div>
      ))}
    </div>
  );
};

const PreviewSkeleton = () => {
  return (
    <div className="space-y-4 p-6">
      <Skeleton className="h-8 w-48 rounded-lg" />
      <Skeleton className="h-4 w-32 rounded-lg" />
      <Skeleton className="h-[420px] w-full rounded-2xl" />
    </div>
  );
};

function MatterTemplatesTableDangerous() {
  const [isAddingTemplate, setIsAddingTemplate] = useState(false);
  const [isEditingTemplate, setIsEditingTemplate] = useState(false);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [isCreateDocxOpen, setIsCreateDocxOpen] = useState(false);
  const [autoOpenNewTemplateUpload, setAutoOpenNewTemplateUpload] =
    useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [isDownloadingSelected, setIsDownloadingSelected] = useState(false);
  const [templateSearch, setTemplateSearch] = useState("");
  const [templatesToDelete, setTemplatesToDelete] = useState<
    MatterTemplate[] | null
  >(null);
  const [templateToDuplicate, setTemplateToDuplicate] =
    useState<MatterTemplate | null>(null);
  const [docxEditorTemplate, setDocxEditorTemplate] =
    useState<MatterTemplate | null>(null);
  const { hasActionPermission } = useUser();
  const createMatterTemplate = useCreateMatterTemplate();
  const deleteMatterTemplate = useDeleteMatterTemplate();
  const bulkDeleteMatterTemplates = useBulkDeleteMatterTemplates();

  const {
    data: templatesResponse,
    isLoading: isTemplatesLoading,
    isError: isTemplatesError,
    isFetching: isTemplatesFetching,
  } = useQuery(retrieveMatterTemplatesQueryOptions());

  const templates = useMemo(
    () =>
      (templatesResponse?.data ?? []).filter(
        (template): template is MatterTemplate =>
          Boolean(template.id && template.name),
      ),
    [templatesResponse?.data],
  );

  const canCreateTemplates = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTemplate,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdateTemplates = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTemplate,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDeleteTemplates = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterTemplate,
    ACTION_PERMISSION_VALUES.delete,
  );
  const canEditTemplateDocuments =
    canUpdateTemplates &&
    hasActionPermission(
      ACTION_PERMISSION_KEYS.matterTemplate,
      ACTION_PERMISSION_VALUES.update,
    );

  const orderedTemplates = useMemo(() => {
    const ordered = [...templates].sort((a, b) => a.name.localeCompare(b.name));

    const query = templateSearch.trim().toLowerCase();
    if (!query) {
      return ordered;
    }

    return ordered.filter((template) =>
      template.name.toLowerCase().includes(query),
    );
  }, [templateSearch, templates]);

  const templateRepoItems = useMemo<RepoItem[]>(
    () =>
      orderedTemplates.map(
        (template) =>
          ({
            id: template.id,
            name: template.name,
            idLocation: "",
            isFolder: false,
            kind: "regular",
          }) as RepoItem,
      ),
    [orderedTemplates],
  );

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === activeTemplateId),
    [activeTemplateId, templates],
  );

  const selectedTemplates = useMemo(
    () => templates.filter((template) => selectedTemplateIds.has(template.id)),
    [selectedTemplateIds, templates],
  );

  const selectedTemplateFileExtension = selectedTemplate
    ? getFileNameParts(
        selectedTemplate.name || selectedTemplate.presignedUrlKey,
      ).fileExtension.toLowerCase()
    : "";

  const isSelectedTemplateDocx = selectedTemplateFileExtension === "docx";
  const canEditSelectedTemplateDocx =
    Boolean(selectedTemplate) &&
    canEditTemplateDocuments &&
    isSelectedTemplateDocx;

  useEffect(() => {
    if (
      activeTemplateId &&
      !templates.some((template) => template.id === activeTemplateId)
    ) {
      setActiveTemplateId(null);
    }
  }, [activeTemplateId, templates]);

  useEffect(() => {
    const availableIds = new Set(templates.map((template) => template.id));
    setSelectedTemplateIds((current) => {
      if ([...current].every((id) => availableIds.has(id))) return current;
      return new Set([...current].filter((id) => availableIds.has(id)));
    });
  }, [templates]);

  useEffect(() => {
    if (selectedTemplateIds.size === 0) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (
        isAddingTemplate ||
        isEditingTemplate ||
        isBulkUploadOpen ||
        isCreateDocxOpen ||
        docxEditorTemplate ||
        templateToDuplicate ||
        templatesToDelete ||
        document.querySelector(
          '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"][data-state="open"]',
        )
      ) {
        return;
      }

      event.preventDefault();
      setSelectedTemplateIds(new Set());
    };

    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [
    selectedTemplateIds.size,
    isAddingTemplate,
    isEditingTemplate,
    isBulkUploadOpen,
    isCreateDocxOpen,
    docxEditorTemplate,
    templateToDuplicate,
    templatesToDelete,
  ]);

  const openCreateSlideover = (options?: { autoOpenUpload?: boolean }) => {
    setAutoOpenNewTemplateUpload(Boolean(options?.autoOpenUpload));
    setIsAddingTemplate(true);
  };

  const handleSetCreateOpen = (open: boolean) => {
    setIsAddingTemplate(open);
    if (!open) {
      setAutoOpenNewTemplateUpload(false);
    }
  };

  const handleCreateDocxTemplate = async (file: File) => {
    const fileNameParts = getFileNameParts(file.name);
    const fileExtension = fileNameParts.fileExtension
      ? `.${fileNameParts.fileExtension}`
      : "";
    const normalizedFileName = fileNameParts.fileName
      ? `${fileNameParts.fileName}${fileExtension}`
      : file.name;

    await createMatterTemplate.mutateAsync({
      name: normalizedFileName,
      description: "",
      category: [],
      file,
    });
  };

  const handleDeleteTemplates = async () => {
    if (!templatesToDelete?.length) return;

    const { deletedIds, failedIds } =
      templatesToDelete.length === 1
        ? await deleteMatterTemplate
            .mutateAsync({ id: templatesToDelete[0].id })
            .then(() => ({
              deletedIds: [templatesToDelete[0].id],
              failedIds: [] as string[],
            }))
        : await bulkDeleteMatterTemplates.mutateAsync(
            templatesToDelete.map((template) => template.id),
          );

    const deletedIdSet = new Set(deletedIds);
    setSelectedTemplateIds(
      (current) => new Set([...current].filter((id) => !deletedIdSet.has(id))),
    );
    if (activeTemplateId && deletedIdSet.has(activeTemplateId)) {
      setActiveTemplateId(null);
    }
    if (docxEditorTemplate && deletedIdSet.has(docxEditorTemplate.id)) {
      setDocxEditorTemplate(null);
    }
    setTemplatesToDelete(null);

    if (templatesToDelete.length > 1) {
      if (failedIds.length > 0) {
        toast.error(
          `Deleted ${deletedIds.length} templates. ${failedIds.length} could not be deleted.`,
        );
      } else {
        toast.success(`Deleted ${deletedIds.length} templates.`);
      }
    }
  };

  const handleDownloadTemplate = async (template: MatterTemplate) => {
    if (!template.presignedUrlKey) {
      toast.error("This template is not available to download.");
      return;
    }

    try {
      const presigned = await generateS3ItemPreSignedUrlV1("matter-template")({
        clientMethod: "get_object",
        key: template.presignedUrlKey,
      });
      await handleDownload(template.name, presigned.data.presignedUrl);
    } catch (error) {
      console.error("Failed to download template", error);
      toast.error("Unable to download this template. Please try again.");
    }
  };

  const handleDownloadSelectedTemplates = async (
    templatesToDownload = selectedTemplates,
  ) => {
    if (templatesToDownload.length === 0 || isDownloadingSelected) return;
    setIsDownloadingSelected(true);

    try {
      await downloadZip(
        templatesToDownload.map((template) => ({
          name: template.name,
          getUrl: async () => {
            if (!template.presignedUrlKey) {
              throw new Error(
                `Template ${template.name} is not available to download.`,
              );
            }
            const presigned = await generateS3ItemPreSignedUrlV1(
              "matter-template",
            )({
              clientMethod: "get_object",
              key: template.presignedUrlKey,
            });
            return presigned.data.presignedUrl;
          },
        })),
        "templates.zip",
      );
    } catch (error) {
      console.error("Failed to download selected templates", error);
      toast.error("Unable to download selected templates. Please try again.");
    } finally {
      setIsDownloadingSelected(false);
    }
  };

  const handleDuplicateSelectedTemplate = () => {
    if (!selectedTemplate) return;
    setTemplateToDuplicate(selectedTemplate);
  };

  const canDownloadSelectedTemplate = Boolean(
    selectedTemplate?.presignedUrlKey,
  );
  const canDuplicateSelectedTemplate =
    canCreateTemplates && canDownloadSelectedTemplate;

  const renderPreviewBody = () => {
    if (isTemplatesLoading || (isTemplatesFetching && !templatesResponse)) {
      return <PreviewSkeleton />;
    }

    if (!selectedTemplate) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
          {canCreateTemplates ? (
            <button
              type="button"
              className="group hover:bg-muted/30 flex flex-col items-center gap-3 rounded-lg px-8 py-6 transition hover:shadow-sm"
              onClick={() => {
                openCreateSlideover({ autoOpenUpload: true });
              }}
            >
              <div className="relative">
                <Folder className="text-muted-foreground/40 h-16 w-16" />
                <div className="bg-primary text-primary-foreground absolute -right-1 -bottom-1 flex h-7 w-7 items-center justify-center rounded-full">
                  <Plus className="h-4 w-4" />
                </div>
              </div>
              <Text variant="body" className="font-semibold">
                Add template
              </Text>
              <Text variant="subHeading">Click to upload a template</Text>
            </button>
          ) : (
            <Text variant="subHeading">
              {templateRepoItems.length === 0
                ? "No templates found."
                : "Select a template to preview it."}
            </Text>
          )}
        </div>
      );
    }

    if (!selectedTemplate.presignedUrlKey) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
          <Text variant="body" className="font-semibold">
            Preview not available.
          </Text>
          <Text variant="subHeading">
            This template does not have a preview link.
          </Text>
        </div>
      );
    }

    return (
      <div className="flex h-full min-h-0 flex-col overflow-hidden p-6">
        {Boolean(
          selectedTemplate.description || selectedTemplate.category?.length,
        ) && (
          <div className="mb-4 space-y-3">
            {selectedTemplate.description ? (
              <Typography className="text-muted-foreground text-sm">
                {selectedTemplate.description}
              </Typography>
            ) : null}
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {(selectedTemplate.category ?? []).map((category) => (
                <Badge key={category} variant="secondary">
                  {category}
                </Badge>
              ))}
            </div>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-hidden">
          <FileViewer
            file={{
              fileName: selectedTemplate.name,
              presignedURLKey: selectedTemplate.presignedUrlKey || "",
            }}
            fileType="matter-template"
            showDownload={false}
            showTitle={false}
            footerClassName="hidden"
            viewerClassName="bg-card min-h-0 items-stretch justify-stretch"
            wrapperClassName="bg-card min-h-0 rounded-none shadow-none"
            compactDocxPreview
            showDocxModeToggle={isSelectedTemplateDocx}
            canEditDocx={canEditSelectedTemplateDocx}
            onDocxEdit={() => {
              if (canEditSelectedTemplateDocx) {
                setDocxEditorTemplate(selectedTemplate);
              }
            }}
          />
        </div>
      </div>
    );
  };

  const HeaderIcon = selectedTemplate
    ? getFileIcon(selectedTemplate.name)
    : Folder;

  return (
    <>
      {isTemplatesError ? (
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      ) : (
        <div className="bg-card flex h-full min-h-0 w-full overflow-hidden rounded-none border">
          <div className="border-border bg-card flex w-[360px] max-w-[360px] min-w-[360px] shrink-0 flex-col border-r xl:w-[420px] xl:max-w-[420px] xl:min-w-[420px]">
            <div className="border-b px-4 py-4">
              {canCreateTemplates ? (
                <RepoUploadSplitButton
                  onUploadFile={() => {
                    openCreateSlideover({ autoOpenUpload: true });
                  }}
                  label="Upload template"
                  buttonIcon={FileIcon}
                  options={[
                    {
                      label: "Create template",
                      icon: FileText,
                      onSelect: () => {
                        setIsCreateDocxOpen(true);
                      },
                    },
                    {
                      label: "Bulk upload",
                      icon: FilesIcon,
                      onSelect: () => {
                        setIsBulkUploadOpen(true);
                      },
                    },
                  ]}
                />
              ) : null}
              <div className={cn("mt-3", !canCreateTemplates && "mt-0")}>
                <Input
                  value={templateSearch}
                  onChange={(event) => {
                    setTemplateSearch(event.target.value);
                  }}
                  placeholder="Search templates..."
                  className="w-full"
                />
              </div>
            </div>

            <ScrollHint
              showOnTop
              className="min-h-0 flex-1"
              contentClassName="h-full min-h-0 overflow-y-auto px-2 py-2"
              fadeClassName="from-black/20 to-transparent"
              hintClassName="hidden"
            >
              {isTemplatesLoading ||
              (isTemplatesFetching && !templatesResponse) ? (
                <ExplorerSkeleton rows={7} />
              ) : templateRepoItems.length > 0 ? (
                <FolderTree
                  matter={{ matterTitle: "Templates" }}
                  items={templateRepoItems}
                  selectionItems={templateRepoItems}
                  onSelectItem={(itemId) => {
                    setActiveTemplateId(itemId || null);
                  }}
                  selectedItemId={activeTemplateId}
                  showCheckboxes
                  selectedItemIds={selectedTemplateIds}
                  onToggleSelection={(items, checked) => {
                    setSelectedTemplateIds((current) => {
                      const next = new Set(current);
                      items.forEach((item) => {
                        if (checked) next.add(item.id);
                        else next.delete(item.id);
                      });
                      return next;
                    });
                  }}
                  onToggleRootSelection={(checked) => {
                    setSelectedTemplateIds((current) => {
                      const next = new Set(current);
                      templateRepoItems.forEach((item) => {
                        if (checked) next.add(item.id);
                        else next.delete(item.id);
                      });
                      return next;
                    });
                  }}
                  onMoveItem={() => false}
                  currentFolderId=""
                  enableDrag={false}
                  isSearching={Boolean(templateSearch.trim())}
                  renderContextMenu={(item, trigger) => {
                    const template = templates.find(
                      (candidate) => candidate.id === item.id,
                    );
                    if (!template) return <>{trigger}</>;

                    return (
                      <MatterTemplateContextMenu
                        items={
                          selectedTemplateIds.has(item.id)
                            ? selectedTemplates
                            : [template]
                        }
                        trigger={trigger}
                        canCreate={canCreateTemplates}
                        canUpdate={canUpdateTemplates}
                        canDelete={canDeleteTemplates}
                        onEdit={(candidate) => {
                          setActiveTemplateId(candidate.id);
                          setIsEditingTemplate(true);
                        }}
                        onDelete={setTemplatesToDelete}
                        onDownload={(items) => {
                          if (items.length === 1) {
                            void handleDownloadTemplate(items[0]);
                          } else {
                            void handleDownloadSelectedTemplates(items);
                          }
                        }}
                        onDuplicate={setTemplateToDuplicate}
                        onEditDocument={setDocxEditorTemplate}
                      />
                    );
                  }}
                />
              ) : (
                <div className="p-2">
                  <Typography className="text-muted-foreground text-sm">
                    No templates found.
                  </Typography>
                </div>
              )}
            </ScrollHint>
            {selectedTemplates.length > 0 ? (
              <FileBulkActions
                selectedCount={selectedTemplates.length}
                onClear={() => {
                  setSelectedTemplateIds(new Set());
                }}
              />
            ) : null}
          </div>

          <div className="bg-card relative flex h-full min-w-0 flex-1 flex-col">
            <div className="border-border flex items-center justify-between gap-4 border-b px-6 py-4">
              <div className="flex min-w-0 items-center gap-2">
                <HeaderIcon
                  className={cn(
                    "h-5 w-5 shrink-0",
                    selectedTemplate ? "text-blue-500" : "text-amber-500",
                  )}
                />
                <Text
                  variant="subHeading"
                  color="foreground"
                  className="mb-0 block max-w-[520px] truncate text-nowrap break-normal"
                  title={selectedTemplate?.name || "Templates"}
                >
                  {selectedTemplate?.name || "Templates"}
                </Text>
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={!selectedTemplate || !canUpdateTemplates}
                  onClick={() => {
                    setIsEditingTemplate(true);
                  }}
                  type="button"
                  title="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center gap-1">
                <TooltipWrapper message="Download">
                  <span className="inline-flex">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (selectedTemplate) {
                          void handleDownloadTemplate(selectedTemplate);
                        }
                      }}
                      type="button"
                      disabled={!canDownloadSelectedTemplate}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </span>
                </TooltipWrapper>
                {canCreateTemplates ? (
                  <TooltipWrapper message="Duplicate">
                    <span className="inline-flex">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleDuplicateSelectedTemplate}
                        type="button"
                        disabled={!canDuplicateSelectedTemplate}
                      >
                        <CopyPlus className="h-4 w-4" />
                      </Button>
                    </span>
                  </TooltipWrapper>
                ) : null}
                <TooltipWrapper message="Delete">
                  <span className="inline-flex">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        if (selectedTemplate) {
                          setTemplatesToDelete([selectedTemplate]);
                        }
                      }}
                      type="button"
                      disabled={!selectedTemplate || !canDeleteTemplates}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </span>
                </TooltipWrapper>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden">
              {renderPreviewBody()}
            </div>
          </div>
        </div>
      )}

      <AddOrEditTemplateModal
        editing={false}
        open={isAddingTemplate}
        setOpen={handleSetCreateOpen}
        autoOpenUpload={autoOpenNewTemplateUpload}
      />

      <BulkUploadMatterTemplatesModal
        open={isBulkUploadOpen}
        setOpen={setIsBulkUploadOpen}
      />

      <CreateDocxSlideover
        open={isCreateDocxOpen}
        setOpen={setIsCreateDocxOpen}
        title="Create template"
        description="Create a new template with fields."
        fileType="matter-template"
        existingFileNames={templates.map((template) => template.name)}
        onSave={handleCreateDocxTemplate}
      />

      {selectedTemplate ? (
        <AddOrEditTemplateModal
          activeMatterTemplate={selectedTemplate}
          isReadOnly={!canUpdateTemplates}
          editing
          open={isEditingTemplate}
          setOpen={setIsEditingTemplate}
          setActiveMatterTemplateId={(id) => {
            setActiveTemplateId(id);
          }}
        />
      ) : null}

      {docxEditorTemplate ? (
        <EditDocxTemplateModal
          open={!!docxEditorTemplate}
          setOpen={(open) => {
            if (!open) {
              setDocxEditorTemplate(null);
            }
          }}
          template={docxEditorTemplate}
        />
      ) : null}

      {templateToDuplicate ? (
        <DuplicateTemplateModal
          open={!!templateToDuplicate}
          setOpen={(open) => {
            if (!open) {
              setTemplateToDuplicate(null);
            }
          }}
          sourceTemplate={templateToDuplicate}
          existingTemplateNames={templates.map((template) => template.name)}
          onDuplicated={(template) => {
            setActiveTemplateId(template.id);
          }}
        />
      ) : null}

      <ActionConfirmationModal
        title={
          templatesToDelete?.length === 1
            ? `Delete ${templatesToDelete[0].name}?`
            : `Delete ${templatesToDelete?.length ?? 0} templates?`
        }
        description="This action cannot be undone."
        open={!!templatesToDelete}
        setOpen={(open) => {
          if (!open) {
            setTemplatesToDelete(null);
          }
        }}
        afterActionFn={handleDeleteTemplates}
      />
    </>
  );
}

export function MatterTemplatesTable() {
  return (
    <ErrorBoundary
      fallback={
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      }
    >
      <MatterTemplatesTableDangerous />
    </ErrorBoundary>
  );
}
